import { useState, useEffect } from 'react';
import { bortleToSky, starRange, bortleBarPct } from '../utils/skyquality.js';
import { usableVerdict, observingPresentation, observingWindow } from '../utils/observations.js';
import WhereToAim from './WhereToAim.jsx';

const API = import.meta.env.VITE_API_BASE || '/api';
const FACTOR_COLOR = { good:'var(--green)', mid:'var(--cyan)', bad:'var(--rose)' };
const fmtCoord = (lat, lon) =>
  `${lat?.toFixed(4)}, ${lon < 0 ? '\u2212' : ''}${Math.abs(lon)?.toFixed(4)}`;

function Rubric({ children, color = 'var(--accent)', aux }) {
  return (
    <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginTop:18 }}>
      <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', letterSpacing:'0.18em', color, textTransform:'uppercase' }}>{children}</span>
      {aux && <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', color:'var(--txt-mute)', letterSpacing:'0.08em' }}>{aux}</span>}
    </div>
  );
}

function DLRow({ k, v, sub, color = 'var(--txt-bright)' }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'104px 1fr', gap:10, padding:'8px 0', borderBottom:'1px solid var(--hair)', alignItems:'baseline' }}>
      <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', letterSpacing:'0.1em', color:'var(--txt-dim)', textTransform:'uppercase' }}>{k}</span>
      <span style={{ fontFamily:'var(--serif)', fontSize: 'var(--ui-body)', color, lineHeight:1.3 }}>
        {v}
        {sub && <small style={{ display:'block', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', marginTop:2, letterSpacing:'0.02em' }}>{sub}</small>}
      </span>
    </div>
  );
}

export default function TonightDetails({ observer, verdict, loading, pass, onPickSite, onActivity, mobile = false }) {
  const rs = mobile ? { ...rootStyle, height:'auto', borderLeft:'none', overflow:'visible' } : rootStyle;
  const scrollStyle = mobile
    ? { padding:'4px 20px 22px' }
    : { overflowY:'auto', flex:1, padding:'4px 20px 22px' };
  const [sites, setSites] = useState(null);
  const [sitesLoading, setSitesLoading] = useState(false);

  useEffect(() => {
    if (!observer) return;
    let c = false; setSitesLoading(true); setSites(null);
    fetch(`${API}/darksites?lat=${observer.lat}&lon=${observer.lon}&radius_km=120`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error()))
      .then(d => { if (!c) { setSites(d); setSitesLoading(false); } })
      .catch(() => { if (!c) { setSites(null); setSitesLoading(false); } });
    return () => { c = true; };
  }, [observer?.lat, observer?.lon]);

  if (!observer) {
    return <div style={rs}><div style={{ padding:'40px 22px', color:'var(--txt-dim)', fontSize: 'var(--ui-body)', textAlign:'center' }}>No location set.</div></div>;
  }

  const cond = verdict?.conditions;
  const bortle = cond?.bortle ?? null;
  const sky = bortleToSky(bortle);
  const inCoverage = bortle != null;
  const barPct = bortleBarPct(bortle) * 100;
  const best = sites?.sites?.[0];

  const verdictColor = { Excellent:'var(--amber)', Good:'var(--green)', Marginal:'var(--cyan)', Poor:'var(--txt-mute)' }[verdict?.verdict] || 'var(--txt-mute)';
  const assessment = observingPresentation(verdict);

  return (
    <div style={rs}>
      {/* header */}
      <div style={{ padding:'18px 20px 14px', borderBottom:'1px solid var(--hair)', flexShrink:0 }}>
        <div style={{ fontFamily:'var(--serif)', fontSize:18, color:'var(--txt-bright)', letterSpacing:'-0.01em' }}>Location Details</div>
        <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--cyan)', letterSpacing:'0.04em', marginTop:8 }}>
          <span style={{ color:'var(--txt-dim)' }}>{'\u2316 '}</span>{fmtCoord(observer.lat, observer.lon)}
        </div>
        {observer.name && <div style={{ fontFamily:'var(--sans)', fontSize: 'var(--ui-body)', color:'var(--txt-dim)', marginTop:3 }}>{observer.name}</div>}
      </div>

      <div style={scrollStyle}>
        {/* where to aim, the focused pass (satellite mode only) */}
        {pass && (
          <div style={{ borderBottom:'1px solid var(--hair)', paddingBottom:12, marginBottom:4 }}>
            <WhereToAim pass={pass} />
          </div>
        )}

        {/* Bortle block */}
        <Rubric aux={inCoverage ? (sky ? null : '') : 'NO COVERAGE'}>Light Pollution · est.</Rubric>
        {inCoverage ? (
          <div style={{ marginTop:11 }}>
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
              <div style={{ fontFamily:'var(--serif)', fontSize:46, lineHeight:0.9, color:'var(--txt-bright)', letterSpacing:'-0.03em' }}>
                {bortle.toFixed(1)}<small style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-body)', color:'var(--txt-dim)', letterSpacing:'0.06em', marginLeft:7 }}>BORTLE</small>
              </div>
              <div style={{ textAlign:'right', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)' }}>
                SQM mag/arcsec²<b style={{ display:'block', color:'var(--txt-bright)', fontSize:15, fontWeight:500, marginTop:2 }}>{sky ? sky.sqm.toFixed(1) : '-'}</b>
              </div>
            </div>
            <div style={{ position:'relative', height:5, marginTop:10, borderRadius:3, background:'linear-gradient(90deg,var(--bg-2) 0%,var(--green) 22%,var(--teal) 38%,var(--amber) 60%,var(--rose) 78%,var(--red) 100%)' }}>
              <span style={{ position:'absolute', top:-3, bottom:-3, width:2, background:'var(--txt-bright)', left:`${barPct}%`, boxShadow:'0 0 6px rgba(0,0,0,0.8)' }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:5, fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', color:'var(--txt-mute)', letterSpacing:'0.08em' }}>
              <span>B1 PRISTINE</span><span>B5 SUBURBAN</span><span>B9 INNER CITY</span>
            </div>
          </div>
        ) : (
          <div style={{ marginTop:11, fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', lineHeight:1.5 }}>
            Light-pollution estimate covers CONUS only. Cloud, moon and twilight still drive the verdict here.
          </div>
        )}

        {/* derived sky quality */}
        {inCoverage && sky && (
          <div style={{ marginTop:16, borderTop:'1px solid var(--hair)' }}>
            <DLRow k="Milky Way" v={sky.milkyway} color={FACTOR_COLOR[sky.mwValence]} />
            <DLRow k="Visible stars" v={starRange(sky.stars)} sub="Naked-eye, zenith, dark-adapted" />
            <DLRow k="Limiting mag" v={`+${sky.nelm.toFixed(1)}`} sub="NELM at zenith" color="var(--cyan)" />

          </div>
        )}

        <DLRow k="Observer" v={assessment.headline} sub={`${observer.name || 'Selected coordinates'} · weighted conditions ${usableVerdict(verdict) ? verdict.score : '-'}/100. ${observingWindow(verdict)}`} color={verdictColor} />
        <div style={{fontSize: 'var(--ui-label)',color:'var(--txt-dim)',lineHeight:1.5}}>{assessment.reason}</div>
        {/* tonight's conditions */}
        {cond && (
          <>
            <Rubric color="var(--cyan)">Tonight&#39;s Conditions</Rubric>
            <div style={{ marginTop:11, borderTop:'1px solid var(--hair)' }}>
              <DLRow k="Cloud" v={cond.cloud_avg_pct != null ? `${cond.cloud_avg_pct}% avg` : 'n/a'} sub={cond.cloud_min_pct != null ? `best window ${cond.cloud_min_pct}%` : null} />
              <DLRow k="Transparency" v={cond.humidity_avg != null ? `${cond.humidity_avg}% RH` : 'n/a'} sub="high humidity softens stars" />
              <DLRow k="Moon" v={`${Math.round((cond.moon_phase_fraction||0)*100)}% ${cond.moon_up ? 'up' : 'down'}`} sub={cond.moon_up ? `altitude ${cond.moon_alt_deg?.toFixed(0)}°` : 'below horizon'} />
              <DLRow k="Darkness" v={cond.darkness || '-'} sub={`sun ${cond.sun_alt_deg?.toFixed(0)}°`} />
              {cond.kp != null && <DLRow k="Kp index" v={cond.kp.toFixed(1)} sub="geomagnetic activity" />}
            </div>
          </>
        )}

        {/* darker skies nearby */}
        <Rubric aux="SITE RANKING">Darker Skies Nearby</Rubric>
        {sitesLoading && <div style={{ padding:'12px 0', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)' }}>Scanning the grid…</div>}
        {!sitesLoading && (!sites || !sites.sites?.length) && (
          <div style={{ padding:'12px 0', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', lineHeight:1.5 }}>
            {sites?.coverage === 'out_of_coverage'
              ? 'Out of light-pollution coverage.'
              : sites?.here_is_dark
              ? `You're already under near-pristine sky (Bortle ${sites?.here?.bortle}) \u2014 nowhere meaningfully darker to travel to.`
              : 'No meaningfully darker cells within range.'}
          </div>
        )}
        {!sitesLoading && sites?.sites?.map((s, i) => {
          const LSAD_STRIP = /\s+(city|town|village|CDP|borough|township|municipality|county|charter township)$/i;
          const displayName = s.name ? s.name.replace(LSAD_STRIP, '').trim() : null;
          const scoreColor = s.score >= 80 ? 'var(--green)' : s.score >= 60 ? 'var(--amber)' : 'var(--txt-dim)';
          return (
            <button key={i} onClick={() => onPickSite?.({ lat:s.lat, lon:s.lon, name: s.name ? `Grid cell near ${s.name}` : `${s.distance_km} km ${s.compass} · Bortle ${s.bortle}` })}
              style={{ display:'block', width:'100%', textAlign:'left', background:'transparent', border:'none', borderBottom:'1px solid var(--hair)', cursor:'pointer', padding:'9px 0' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                <span style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize: 'var(--ui-body)', color:'var(--txt-bright)' }}>
                  {displayName ? `Grid cell near ${displayName}` : `Grid cell ~${s.distance_km} km ${s.compass}`}
                </span>
                {s.score != null && (
                  <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:scoreColor, letterSpacing:'0.04em' }}>rank {s.score}</span>
                )}
              </div>
              <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', color:'var(--txt-dim)', letterSpacing:'0.03em', marginTop:2 }}>
                Bortle <span style={{ color:scoreColor }}>{s.bortle}</span>
                {s.bortle_label ? ` ${s.bortle_label}` : ''} · {s.distance_km} km {s.compass}
                <br />{fmtCoord(s.lat,s.lon)} · site rank uses darkness gain and distance, not weather
                {s.ida_designation ? <span style={{ color:'var(--amber)', marginLeft:4 }}>★ IDA</span> : null}
              </div>
            </button>
          );
        })}
        {sites?.sites?.length > 0 && (
          <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', color:'var(--txt-mute)', letterSpacing:'0.03em', marginTop:8, lineHeight:1.4 }}>
            Straight-line distance · VIIRS estimate · drive times not yet modeled. Tap a site to re-read the sky there.
          </div>
        )}

        {/* cross-links */}
        <div style={{ display:'flex', gap:8, marginTop:18 }}>
          <button onClick={() => onActivity?.('iss')} style={{ flex:1, padding:'10px', textAlign:'center', fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', letterSpacing:'0.12em', border:'1px solid var(--accent)', background:'var(--accent)', color:'#0e0e0e', cursor:'pointer' }}>PASSES FROM OBSERVER →</button>
          {best && <button onClick={() => onPickSite?.({ lat:best.lat, lon:best.lon, name: best.name ? `Grid cell near ${best.name}` : `${best.distance_km} km ${best.compass} · Bortle ${best.bortle}` })} style={{ flex:1, padding:'10px', textAlign:'center', fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', letterSpacing:'0.12em', border:'1px solid var(--accent)', background:'transparent', color:'var(--accent)', cursor:'pointer' }}>USE NEARBY CELL {best.compass}</button>}
        </div>

        {(
          <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', color:'var(--txt-mute)', marginTop:14, lineHeight:1.5 }}>
            Bortle is a static VIIRS estimate. Nearby cells are suggestions, not verified observing venues; check access before travel.
          </div>
        )}
      </div>
    </div>
  );
}

const rootStyle = {
  display:'flex', flexDirection:'column', height:'100%',
  borderLeft:'1px solid var(--hair)', background:'var(--bg)', overflow:'hidden',
};
